import { Howl } from 'howler';

let hasUserInteracted = false;

const sounds = {
  match: new Howl({ src: ['/sounds/match.mp3'] }),
  stripe: new Howl({ src: ['/sounds/stripe.mp3'] }),
  bomb: new Howl({ src: ['/sounds/bomb.mp3'] }),
  win: new Howl({ src: ['/sounds/win.mp3'] }),
};

export const initializeGrid = (width, height, obstacles) => {
  const grid = Array(height).fill().map(() => Array(width).fill(null));
  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      if (obstacles.some(([oRow, oCol]) => oRow === row && oCol === col)) {
        grid[row][col] = { color: 0, type: 'obstacle' };
      } else {
        grid[row][col] = { color: Math.floor(Math.random() * 6) + 1, type: 'regular' };
      }
    }
  }
  return removeInitialMatches(grid);
};

const removeInitialMatches = (grid) => {
  const height = grid.length;
  const width = grid[0].length;
  let hasMatches;
  do {
    hasMatches = false;
    const matches = findMatches(grid);
    if (matches.length > 0) {
      hasMatches = true;
      matches.forEach(match => {
        match.forEach(([row, col]) => {
          let newColor;
          do {
            newColor = Math.floor(Math.random() * 6) + 1;
          } while (
            (col > 1 && grid[row][col - 1]?.color === newColor && grid[row][col - 2]?.color === newColor) ||
            (col < width - 2 && grid[row][col + 1]?.color === newColor && grid[row][col + 2]?.color === newColor) ||
            (row > 1 && grid[row - 1][col]?.color === newColor && grid[row - 2][col]?.color === newColor) ||
            (row < height - 2 && grid[row + 1][col]?.color === newColor && grid[row + 2][col]?.color === newColor)
          );
          grid[row][col] = { color: newColor, type: 'regular' };
        });
      });
    }
  } while (hasMatches);
  return grid;
};

export const swapBlocks = (grid, pos1, pos2) => {
  const [row1, col1] = pos1;
  const [row2, col2] = pos2;
  if (Math.abs(row1 - row2) + Math.abs(col1 - col2) !== 1) return grid;
  const newGrid = grid.map(row => [...row]);
  [newGrid[row1][col1], newGrid[row2][col2]] = [newGrid[row2][col2], newGrid[row1][col1]];
  return newGrid;
};

export const findMatches = (grid) => {
  const matches = [];
  const height = grid.length;
  const width = grid[0].length;

  // Horizontal matches
  for (let row = 0; row < height; row++) {
    let match = [];
    for (let col = 0; col < width; col++) {
      if (
        col > 0 &&
        grid[row][col]?.color &&
        grid[row][col].color === grid[row][col - 1]?.color &&
        grid[row][col].type !== 'obstacle' &&
        grid[row][col - 1].type !== 'obstacle'
      ) {
        match.push([row, col]);
      } else {
        if (match.length >= 3) matches.push(match);
        match = [[row, col]];
      }
    }
    if (match.length >= 3) matches.push(match);
  }

  // Vertical matches
  for (let col = 0; col < width; col++) {
    let match = [];
    for (let row = 0; row < height; row++) {
      if (
        row > 0 &&
        grid[row][col]?.color &&
        grid[row][col].color === grid[row - 1][col]?.color &&
        grid[row][col].type !== 'obstacle' &&
        grid[row - 1][col].type !== 'obstacle'
      ) {
        match.push([row, col]);
      } else {
        if (match.length >= 3) matches.push(match);
        match = [[row, col]];
      }
    }
    if (match.length >= 3) matches.push(match);
  }

  return matches;
};

export const handleMatches = (grid, matches) => {
  let score = 0;
  const newGrid = grid.map(row => [...row]);
  matches.forEach(match => {
    console.log('Match found:', match);
    match.forEach(([row, col]) => {
      if (newGrid[row][col]?.type === 'bomb') {
        score += applyBomb(newGrid, row, col);
      } else if (newGrid[row][col]?.type === 'stripe_horizontal') {
        score += clearRow(newGrid, row);
      } else if (newGrid[row][col]?.type === 'stripe_vertical') {
        score += clearColumn(newGrid, col);
      }
      newGrid[row][col] = null;
      score += 10;
    });

    if (match.length >= 5) {
      const [row, col] = match[0];
      newGrid[row][col] = { color: grid[row][col].color, type: 'bomb' };
      console.log('Created bomb at', row, col);
      if (hasUserInteracted) sounds.bomb.play();
    } else if (match.length === 4) {
      const [row, col] = match[0];
      const isHorizontal = match.every(([r]) => r === row);
      newGrid[row][col] = {
        color: grid[row][col].color,
        type: isHorizontal ? 'stripe_horizontal' : 'stripe_vertical',
      };
      console.log('Created stripe at', row, col, isHorizontal ? 'horizontal' : 'vertical');
      if (hasUserInteracted) sounds.stripe.play();
    } else if (hasUserInteracted) {
      sounds.match.play();
    }
  });
  return { grid: newGrid, score };
};

export const applyBomb = (grid, row, col) => {
  let score = 0;
  for (let r = Math.max(0, row - 1); r <= Math.min(grid.length - 1, row + 1); r++) {
    for (let c = Math.max(0, col - 1); c <= Math.min(grid[0].length - 1, col + 1); c++) {
      if (grid[r][c] && grid[r][c].type !== 'obstacle') {
        grid[r][c] = null;
        score += 20;
      }
    }
  }
  if (hasUserInteracted) sounds.bomb.play();
  return score;
};

export const clearRow = (grid, row) => {
  let score = 0;
  for (let col = 0; col < grid[0].length; col++) {
    if (grid[row][col] && grid[row][col].type !== 'obstacle') {
      grid[row][col] = null;
      score += 15;
    }
  }
  if (hasUserInteracted) sounds.stripe.play();
  return score;
};

export const clearColumn = (grid, col) => {
  let score = 0;
  for (let row = 0; row < grid.length; row++) {
    if (grid[row][col] && grid[row][col].type !== 'obstacle') {
      grid[row][col] = null;
      score += 15;
    }
  }
  if (hasUserInteracted) sounds.stripe.play();
  return score;
};

export const dropBlocks = (grid) => {
  const height = grid.length;
  const width = grid[0].length;
  const newGrid = grid.map(row => [...row]);
  for (let col = 0; col < width; col++) {
    let emptyRow = height - 1;
    for (let row = height - 1; row >= 0; row--) {
      if (newGrid[row][col] && newGrid[row][col].type !== 'obstacle') {
        newGrid[emptyRow][col] = newGrid[row][col];
        if (emptyRow !== row) newGrid[row][col] = null;
        emptyRow--;
      }
    }
    while (emptyRow >= 0) {
      newGrid[emptyRow][col] = { color: Math.floor(Math.random() * 6) + 1, type: 'regular' };
      emptyRow--;
    }
  }
  return newGrid;
};

export const setUserInteracted = () => {
  hasUserInteracted = true;
};